import { Injectable } from '@angular/core';
import { TableColumn } from '@shared/interface/table.interface';
import { PendingRegistrationColumns, PendingSubmissionTableColumns, RejectTableColumns, TerverifikasiTableColumns, TerverifikasiNonActiveColumn } from './retailer.data';
import { BaseService } from '@services/base-service.service';
import { IRetailerArea, IRetailerDetail, IRetailerSubArea } from '@models/retailer.model';
import { API } from '@config/constants/api.constant';
import { BehaviorSubject, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { IListboxBase, IListboxRegion } from '@shared/components/form/input-listbox-region/input-listbox-region.interface';
import { IQueryParams } from '@shared/interface/urlparam.interface';
import {IDataInputQR, IFormRegisterMAC, IMitraListMAC, IPayloadRegisterMAC} from './retailers.interface';
import { FormBuilder, Validators } from '@angular/forms';
import { UtilitiesService } from '@services/utilities.service';
import { Sort } from '@angular/material/sort';
import { FilterService } from '@services/filter.service';
import { ISelectRetailer } from '@shared/components/form/input-select/input-select.interface';
import { IListReject } from '../distributor/interfaces/distributor.interface';
import { IProgressScan } from '../sales-marketing/program-marketing/program-marketing-retailer/program-marketing-retailer.interface';
import { IDownloadCsv } from './retailers.interface';

@Injectable({
  providedIn: 'root',
})
export class RetailerService {
  private detailRetailerSubject = new BehaviorSubject({} as IRetailerDetail);
  detailRetailer$ = this.detailRetailerSubject.asObservable();

  private formRegisterMAC = this.fb.group<IFormRegisterMAC>({
    retailer_id: this.fb.nonNullable.control('', Validators.required),
    sub_district_id: this.fb.nonNullable.control([], Validators.required),
    npwp: this.fb.nonNullable.control({ value: '', disabled: false }, Validators.required),
    npwp_image_url: this.fb.nonNullable.control(''),
  });
  macFormRegisterSubject = new BehaviorSubject(this.formRegisterMAC);

  private macSelectedRegion = [] as IListboxRegion[];
  macSelectedRegionSubject = new BehaviorSubject(this.macSelectedRegion);

  private macMitraListSubject = new BehaviorSubject([] as IMitraListMAC[]);
  macMitraList$ = this.macMitraListSubject.asObservable();

  // temp.disabled mac related
  isMACDisabled = false;

  get MacFormRegister() {
    return this.macFormRegisterSubject.value;
  }

  get MacSelectedRegion() {
    return this.macSelectedRegionSubject.value;
  }

  set MacSelectedRegion(data) {
    this.macSelectedRegionSubject.next(data);
  }

  get RetailerDetail() {
    return this.detailRetailerSubject.value;
  }

  set RetailerDetail(data) {
    this.detailRetailerSubject.next(data);
  }

  get MacMitraList() {
    return this.macMitraListSubject.value;
  }

  set MacMitraList(data) {
    this.macMitraListSubject.next(data);
  }

  constructor(private router: Router, private fb: FormBuilder, private baseService: BaseService, private utils: UtilitiesService, private filterService: FilterService) {}

  getDetailByApi(id: string, api: string) {
    return this.baseService.getData<IRetailerDetail>(api + id).pipe(
      shareReplay(),
      map((resp) => {
        if (resp && resp.success) {
          this.RetailerDetail = resp.data;
        }

        return resp;
      })
    );
  }

  getDetailById(id: string) {
    return this.baseService.getData<IRetailerDetail>(API.DETAIL_RETAILER + id).pipe(
      shareReplay(),
      map((resp) => {
        if (resp && resp.success) {
          this.RetailerDetail = resp.data;
        }

        return resp;
      })
    );
  }

  getRetailerArea() {
    return this.baseService.getData<IRetailerArea[]>(API.LIST_AREA_RETAILER).pipe(shareReplay());
  }

  getRetailerSubArea(areaID: string) {
    return this.baseService.getData<IRetailerSubArea[]>(API.LIST_SUB_AREA_RETAILER + '/' + areaID).pipe(shareReplay());
  }

  getTerverifikasiTableColumns(withDate = true) {
    const table: TableColumn[] = TerverifikasiTableColumns;

    // temp.disable mac
    const _MACTable = this.isMACDisabled ? table.filter((item) => item.key !== 'is_mac') : table;
    // temp.disable date
    return !withDate ? _MACTable.filter((item) => item.key !== 'createdDate') : _MACTable;
  }

  getTerverifikasiNonactiveTableColumns = () => TerverifikasiNonActiveColumn;

  getPendingRegistrationTableColumns = () => PendingRegistrationColumns;

  getPendingSubmissionTableColumns = () => PendingSubmissionTableColumns;

  getRejectTableColumns = () => RejectTableColumns;

  goToMACRegistration(id: string, status?: string) {
    this.resetFormRegisterMAC();
    return this.router.navigate(['/retailer/registrasi-mac'], {
      queryParams: { id, status },
    });
  }

  goToRetailerDetail(id: string, status: string) {
    return this.router.navigate([`/retailer/${status}/${id}`]);
  }

  generateMacInfo(data: IRetailerDetail) {
    const { mac_scope, register_mac_date, mac_code } = data;
    return [
      {
        label: 'ID User',
        key: 'mac_code',
        value: mac_code,
      },
      {
        label: 'Cakupan',
        key: 'mac_scope',
        value: mac_scope,
      },
      {
        label: 'Bergabung MAC Sejak',
        key: 'register_mac_date',
        value: register_mac_date,
      },
    ];
  }

  getMACAreaWithParam(id: string, param: IQueryParams) {
    const { page, size, string_filter } = param;
    let _param = '?';

    _param += string_filter ? 'string_filter=' + string_filter : '';
    _param += page ? '&page=' + page : '';
    _param += size ? '&size=' + size : '';

    const _query = _param ? id + _param : id;
    return this.baseService.getData<IListboxBase[]>(API.MAC_RETAILER_LIST_AREA + _query).pipe(
      shareReplay(),
      map((resp) => {
        return resp;
      })
    );
  }

  registerMAC(payload: IPayloadRegisterMAC) {
    return this.baseService.postData<IPayloadRegisterMAC>(API.MAC_RETAILER_REGISTER, payload).pipe(
      map((resp) => {
        return resp;
      })
    );
  }

  getMitraList(payload: { district_id: string[] }) {
    return this.baseService.postData<IMitraListMAC[]>(API.MAC_RETAILER_LIST_MITRA, payload).pipe(
      map((resp) => {
        return resp;
      })
    );
  }

  mapSelectedID(items: IListboxRegion[]): string[] {
    return [...new Set(items.flatMap((item) => item.id))];
  }

  renderMitraList(selectedItems: string[]) {
    const _separator = selectedItems.length === 2 ? '&' : ',';
    return this.utils.arrayStringJoin(selectedItems, '', _separator, true);
  }

  resetFormRegisterMAC = () => this.formRegisterMAC.reset();

  sortTable(tableColumns: TableColumn[], param: Sort) {
    const sortBy = tableColumns.find((column) => column.key === param.active);
    param.active = sortBy ? sortBy.key : '';
    return this.filterService.sortDataSource(param);
  }

  mapInputSelectRetailerArea(data: IRetailerArea[]) {
    return data?.map((item) => {
      const inputSelector = new ISelectRetailer();
      inputSelector.setOptions(item);
      return inputSelector;
    });
  }

  getRetailerRejectionEnum(retailerId: string) {
    const params = { retailerId };
    return this.baseService.getData<IListReject[]>(API.LIST_REJECT_RETAILER, undefined, params);
  }

  postRetailerQR(retailerId: string, payload:IDataInputQR) {
    return this.baseService
      .postData<{ success:boolean, message?:string, errors?:any }>(`${API.POST_RETAILER_QR}/${retailerId}`, payload, { disableModalPopupError: true })
      .pipe(shareReplay());
  }

  getInitialDetailProgressScanRetailer(program_id: string, retailer_id: string) {
    const params = program_id + "/" + retailer_id;
    return this.baseService.getData<IProgressScan>(API.PROGRAM_MARKETING_RETAILER.DETAIL_PROGRESS_SCAN_RETAILER + params).pipe(
      map((resp) => {
        return resp;
      })
    );
  }

  getDownloadCSV(program_id: string, retailer_id: string) {
    const params = program_id+"/"+retailer_id;
    return this.baseService.getData<IDownloadCsv[]>(API.PROGRAM_MARKETING_RETAILER.DOWNLOAD_PROGRESS_SCAN_RETAILER + params).pipe(
      map((resp) => {
        return resp;
      })
    );
  }
}
